class PredictiveSearch extends HTMLElement {
  constructor() {
    super()

    this.cachedResults = {}
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')
    this.popularRequest = this.querySelector('.popular-requests')
    this.isOpen = false
    this.requestNumber = 0
    this.resultsTerm = ''
    this.idPrefix = `hg-predictive-${++PredictiveSearch.instanceCount}-`

    this.setupEventListeners()
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    )

    this.input.addEventListener('input', () => {
      this.requestNumber++
      this.removeAttribute('results')
      this.close()
    })

    this.input.addEventListener(
      'input',
      debounce(this.onChange.bind(this), 300)
    )

    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
  }

  getQuery() {
    return this.input.value.trim()
  }

   normalizeQuery(value) {
    const compact = value.toLowerCase().replace(/[\s-]+/g, '')

    if (
      compact.length >= 5 &&
      (
        'bestseller'.startsWith(compact) ||
        'bestsellers'.startsWith(compact) ||
        'bestselling'.startsWith(compact)
      )
    ) {
      return 'best seller'
    }

    return value
  }

  onChange() {
    const searchTerm = this.getQuery()

    if (!searchTerm) {
      this.removeAttribute('loading')
      this.close(true)
      return
    }

    this.getSearchResults(searchTerm)
  }

  onFormSubmit(event) {
    const query = this.getQuery()

    if (!query) {
      event.preventDefault()
      return
    }

    this.input.value = this.normalizeQuery(query)
  }

  onFocus() {
    const searchTerm = this.getQuery()

    if (!searchTerm) {
      if (this.popularRequest) this.open()
      return
    }

    if (
      this.getAttribute('results') === 'true' &&
      this.resultsTerm === searchTerm
    ) {
      this.open()
    } else {
      this.getSearchResults(searchTerm)
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close()
    })
  }

  onKeydown(event) {
    if (event.code === 'Escape') {
      event.preventDefault()
      this.close()
      return
    }

    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
      this.switchOption(event.code === 'ArrowUp' ? 'up' : 'down')
    }

    if (event.code === 'Enter' && this.isOpen) {
      const link = this.predictiveSearchResults.querySelector(
        '[aria-selected="true"] a'
      )

      if (link) {
        event.preventDefault()
        link.click()
      }
    }
  }

  switchOption(direction) {
    if (!this.isOpen) return

    const options = Array.from(
      this.predictiveSearchResults.querySelectorAll('[role="option"]')
    ).filter(
      option =>
        option.getClientRects().length &&
        option.querySelector('a, button')
    )

    if (!options.length) return

    const current = options.findIndex(
      option => option.getAttribute('aria-selected') === 'true'
    )

    if (direction === 'up' && current === -1) return

    const nextIndex =
      direction === 'down'
        ? (current + 1) % options.length
        : (current - 1 + options.length) % options.length

    options.forEach(option => {
      option.setAttribute('aria-selected', 'false')
    })

    const active = options[nextIndex]

    active.setAttribute('aria-selected', 'true')
    this.input.setAttribute('aria-activedescendant', active.id)
    this.setLiveRegionText(active.textContent.trim())
    active.scrollIntoView({ block: 'nearest' })
  }

  getSearchResults(searchTerm) {
    const requestNumber = ++this.requestNumber
    const normalizedTerm = this.normalizeQuery(searchTerm)
    const queryKey = normalizedTerm.toLowerCase()

    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm)
      return
    }

    const params = new URLSearchParams({
      q: normalizedTerm,
      'resources[type]': 'query,product,collection',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      section_id: 'predictive-search'
    })

    fetch(`${routes.predictive_search_url}?${params.toString()}`)
      .then(response => {
        if (!response.ok) throw new Error(response.status)
        return response.text()
      })
      .then(text => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')

        if (!section) {
          throw new Error('Predictive search section is missing')
        }

        this.cachedResults[queryKey] = section.innerHTML

        if (
          requestNumber !== this.requestNumber ||
          this.getQuery() !== searchTerm
        ) {
          return
        }

        this.renderSearchResults(section.innerHTML, searchTerm)
      })
      .catch(error => {
        if (requestNumber !== this.requestNumber) return

        this.removeAttribute('loading')
        this.close()
        console.error('Predictive search:', error)
      })
  }

  setLiveRegionLoadingState() {
    this.setLiveRegionText(
      this.getAttribute('data-loading-text') || 'Loading suggestions'
    )

    this.setAttribute('loading', true)
  }

  setLiveRegionText(statusText) {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status')

    if (!this.statusElement) return

    clearTimeout(this.liveRegionTimer)

    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText

    this.liveRegionTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    this.predictiveSearchResults.innerHTML = resultsMarkup

    const idMap = new Map()

    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      idMap.set(element.id, this.idPrefix + element.id)
      element.id = this.idPrefix + element.id
    })

    const referenceAttributes = ['aria-labelledby', 'aria-describedby']

    this.predictiveSearchResults
      .querySelectorAll('[aria-labelledby], [aria-describedby]')
      .forEach(element => {
        referenceAttributes.forEach(attribute => {
          if (!element.hasAttribute(attribute)) return

          element.setAttribute(
            attribute,
            element
              .getAttribute(attribute)
              .split(/\s+/)
              .map(id => idMap.get(id) || id)
              .join(' ')
          )
        })
      })

    const results = this.predictiveSearchResults.querySelector(
      '.hg-search-results'
    )

    if (results) {
      results.style.padding =
        window.innerWidth < 750 ? '16px' : '18px 22px'

      this.input.setAttribute('aria-controls', results.id)
    }

    this.input.removeAttribute('aria-activedescendant')
    this.resultsTerm = searchTerm
    this.setAttribute('results', true)
    this.removeAttribute('loading')

    const count = this.predictiveSearchResults.querySelector(
      '[data-predictive-search-live-region-count-value]'
    )

    if (count) this.setLiveRegionText(count.textContent.trim())
    if (this.contains(document.activeElement)) this.open()
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header')

    const bottom = header
      ? header.getBoundingClientRect().bottom
      : this.input.getBoundingClientRect().bottom

    return Math.max(
      120,
      Math.min(window.innerHeight * .7, window.innerHeight - bottom - 20)
    )
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsMaxHeight()}px`

    this.setAttribute('open', true)
    this.input.setAttribute('aria-expanded', 'true')
    this.isOpen = true
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
    }

    this.predictiveSearchResults
      .querySelectorAll('[aria-selected="true"]')
      .forEach(option => {
        option.setAttribute('aria-selected', 'false')
      })

    this.input.removeAttribute('aria-activedescendant')
    this.removeAttribute('open')
    this.input.setAttribute('aria-expanded', 'false')
    this.predictiveSearchResults.style.removeProperty('max-height')
    this.isOpen = false
  }
}

PredictiveSearch.instanceCount = 0
customElements.define('predictive-search', PredictiveSearch)